import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

interface Advertisement {
  id: number;
  title: string;
  type: string;
  price: string | null;
  category: string;
  description: string;
  contact_phone: string;
  province: string | null;
  status: string;
  views: number;
  created_at: string;
  user_name?: string;
}

const AdvertisementDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [ad, setAd] = useState<Advertisement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    
    fetch(`https://fellfell.ir/api/advertisements.php?action=single&id=${id}`, {
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => {
        console.log('📦 پاسخ آگهی:', data);
        if (data.success && data.data) {
          setAd(data.data);
        } else {
          setError(data.message || 'آگهی یافت نشد');
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('❌ خطا:', err);
        setError('خطا در بارگذاری آگهی');
        setLoading(false);
      });
  }, [id]);

  // ── تابع دریافت برچسب نوع ──────────────────────────────
  const getTypeLabel = (type: string) => {
    const types: Record<string, { label: string; color: string }> = {
      'sell': { label: 'فروش', color: '#2e7d32' },
      'buy': { label: 'خرید', color: '#1565c0' },
      'request': { label: 'درخواست', color: '#f57c00' },
      'cooperation': { label: 'همکاری', color: '#6a1b9a' }
    };
    return types[type] || { label: type, color: '#333' };
  };

  // ── تابع دریافت برچسب وضعیت ──────────────────────────────
  const getStatusInfo = (status: string) => {
    const statusMap: Record<string, { label: string; color: string }> = {
      'approved': { label: '✅ تایید شده', color: '#2e7d32' },
      'pending': { label: '⏳ در انتظار تایید', color: '#f57c00' },
      'rejected': { label: '❌ رد شده', color: '#c62828' },
      'expired': { label: '⏰ منقضی', color: '#757575' }
    };
    return statusMap[status] || { label: status || 'نامشخص', color: '#333' };
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        ⏳ در حال بارگذاری آگهی...
      </div>
    );
  }

  if (error || !ad) {
    return (
      <div style={{ padding: '20px', color: '#c62828', background: '#ffebee', borderRadius: '10px' }}>
        ❌ {error || 'آگهی یافت نشد'}
      </div>
    );
  }

  const type = getTypeLabel(ad.type);
  const status = getStatusInfo(ad.status || 'pending');

  return (
    <>
      <div style={{ background: '#1e3a1e', color: '#fff', padding: '40px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <h1 style={{ fontSize: '28px' }}>📢 {ad.title}</h1>
        </div>
      </div>
      
      <section style={{ padding: '30px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ 
            background: '#fff', 
            borderRadius: '16px', 
            padding: '30px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.06)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #eee' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <span style={{ padding: '5px 15px', background: '#e8f5e9', color: type.color, borderRadius: '20px', fontSize: '13px', fontWeight: '600' }}>
                  {type.label}
                </span>
                <span style={{ padding: '5px 15px', background: '#f5f5f5', color: status.color, borderRadius: '20px', fontSize: '13px' }}>
                  {status.label}
                </span>
              </div>
              <div style={{ color: '#888', fontSize: '14px' }}>
                👤 {ad.user_name || 'کاربر'} | 
                📅 {new Date(ad.created_at).toLocaleDateString('fa-IR')} |
                👁️ {ad.views || 0} بازدید
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
              <div>
                <strong style={{ color: '#555' }}>دسته‌بندی:</strong>
                <span style={{ marginRight: '10px', color: '#333' }}>{ad.category || 'متفرقه'}</span>
              </div>
              <div>
                <strong style={{ color: '#555' }}>استان:</strong>
                <span style={{ marginRight: '10px', color: '#333' }}>{ad.province || 'نامشخص'}</span>
              </div>
              <div>
                <strong style={{ color: '#555' }}>قیمت:</strong>
                <span style={{ marginRight: '10px', color: '#2e7d32', fontWeight: 'bold' }}>
                  {ad.price ? new Intl.NumberFormat('fa-IR').format(Number(ad.price)) + ' تومان' : 'توافقی'}
                </span>
              </div>
              <div>
                <strong style={{ color: '#555' }}>شماره تماس:</strong>
                <span style={{ marginRight: '10px', color: '#333', direction: 'ltr' }}>{ad.contact_phone}</span>
              </div>
            </div>

            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
              <h3 style={{ color: '#1e3a1e', fontSize: '16px', marginBottom: '10px' }}>📝 توضیحات</h3>
              <p style={{ lineHeight: '2', fontSize: '15px', color: '#333' }}>
                {ad.description}
              </p>
            </div>

            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
              <Link to="/advertisements" style={{ color: '#2e7d32', textDecoration: 'none' }}>
                ← بازگشت به لیست آگهی‌ها
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AdvertisementDetail;